/**
 * The root component of the QuickPoint Author.
 * The URL structure should always be TLD/author/owner/stack
 * The URL is the source of truth for the current stack.
 * @module App
 * @category Display Components
 */

import { withAttributionUtms } from "./lib/conversionTracking";
import { rybbitPageview } from "./lib/rybbit";

import { notify,
	NormalizeCss,
	clientConfig,
	getOwnerName,
	getStackFromUrl,
	strings,
} from "@quickpoint.me/shared";
import { initializeApp } from "firebase/app";
import { getAuth, getIdToken, onAuthStateChanged } from "firebase/auth";
import { customAlphabet, urlAlphabet } from "nanoid";
import { Suspense, useEffect, useState } from "react";
import { scan } from "react-scan";
import { type MutableSnapshot, RecoilRoot } from "recoil";
import LogSuspense from "./displayComponents/LogSuspense";
import RefreshSpinner from "./displayComponents/Refresh";
import { UserInterface } from "./displayComponents/UserInterface";
import RenderPreviewGenerator from "./displayComponents/previewbar/RenderPreviewGenerator";
import BabySitter from "./functionalComponents/BabySitter";
import LoadData from "./functionalComponents/LoadData";
import Messages from "./functionalComponents/Messages";
import SendStackToViewer from "./functionalComponents/SendStackToViewer";
import UrlManager from "./functionalComponents/UrlManager";
import { NoticeHost } from "./displayComponents/NoticeHost";
import GoldStandardGenerator from "./displayComponents/GoldStandardGenerator";
import { HostId } from "./state/atoms";
import {
	Authorization,
	type AuthorizationType,
	OwnerNameAtom,
	PersistOwnerNameAtom,
	firebaseConfig,
} from "./state/authAtoms";
import { StackCfKeyAtom } from "./state/stackAtoms";
import { OpenedInTabStories } from "./state/uiAtoms";

scan({
	enabled: false,
	log: true, // logs render info to console (default: false)
});

//save the query string to the local storage BEFORE the router strips it
const queryString = window.location.search;
if (queryString) {
	sessionStorage.setItem("queryString", queryString);
}

// Rybbit init is async; rybbitPageview waits for it (see lib/rybbit.ts).
// withAttributionUtms backfills campaign UTMs from the qp_attr cookie when the
// live URL doesn't carry them, so cookie-only attribution still reaches Rybbit.
rybbitPageview(
	`${window.location.pathname}${withAttributionUtms(queryString)}`,
);

/**
 * @export App
 */

const authorization: AuthorizationType = {
	initializingFireBaseUser: true,
	isLoggedIn: false,
	uid: undefined,
};

function App() {
	const [authorizing, setAuthorizing] = useState(true);
	const [ownerName, setOwnerName] = useState<string>(strings.unset);

	useEffect(() => {
		async function getFont(name: string) {
			const url = `${clientConfig.getFont()}/${name}.woff2`;
			try {
				const fontContent = await fetch(url).then((r) => r.arrayBuffer());
				const newFont = new FontFace(name, fontContent);
				await newFont.load();
				document.fonts.add(newFont);
			} catch (e) {
				console.log(e);
			}
		}
		try {
			const fonts = ["Avantt-Regular"];
			// biome-ignore lint/complexity/noForEach: this is more readable
			fonts.forEach((name) => getFont(name));
		} catch (e) {
			console.log(e);
		}
	});

	useEffect(() => {
		const app = initializeApp(firebaseConfig);
		const fbAuth = getAuth(app);
		const unsubscribe = onAuthStateChanged(fbAuth, async (user) => {
			authorization.initializingFireBaseUser = false;
			if (user?.uid) {
				authorization.isLoggedIn = true;
				authorization.uid = user.uid;
				const jwt = await getIdToken(user);
				try {
					const getOwner = await getOwnerName(jwt);
					if (getOwner.owners && getOwner.owners.length === 0) {
						// oops the user hasn't registered a name.
						// this can happen by logging in with OAuth.. Firebase creates an account anyway.
						setOwnerName(strings.utility.noOwnerNameAssociatedWithEmail);
					}
					if (getOwner.owners && getOwner.owners.length > 0) {
						setOwnerName(getOwner.owners[0]);
					}
					setAuthorizing(false);
				} catch (e) {
					notify(
						"There is an error with the server. Please refresh or try again later.",
					);
					throw new Error("server error");
				}
			} else {
				setAuthorizing(false);
			}
		});
		return unsubscribe;
	}, []);

	if (authorizing) {
		return <RefreshSpinner showBackGround={true} fullScreen backspin />;
	}

	const initializeState = ({ set }: { set: MutableSnapshot["set"] }) => {
		const stackFromUrl = getStackFromUrl(window.location.pathname);
		const nanoid = customAlphabet(urlAlphabet, 10);
		set(Authorization, authorization);
		if (ownerName) {
			set(OwnerNameAtom, ownerName);
			set(PersistOwnerNameAtom, ownerName);
		}
		set(HostId, nanoid());

		set(
			StackCfKeyAtom,
			stackFromUrl.validStackCfKey ? stackFromUrl.stackCfKey : "",
		);

		set(
			OpenedInTabStories,
			stackFromUrl.stackCfKey && stackFromUrl.stackFileName !== strings.unset
				? [stackFromUrl.stackCfKey]
				: [],
		);
	};

	return (
		<>
			<NormalizeCss />
			<NoticeHost />
			<RecoilRoot initializeState={initializeState}>
				<Suspense fallback={<LogSuspense />}>
					<BabySitter />
					<RenderPreviewGenerator />
					<UrlManager />
					<Messages />
					<SendStackToViewer />
					<LoadData />
				</Suspense>
				<UserInterface />
				{/* dev/staging-only tool; the component self-gates out of prod */}
				<Suspense fallback={null}>
					<GoldStandardGenerator />
				</Suspense>
			</RecoilRoot>
		</>
	);
}
export { App };
